import React, { useEffect, useRef, useState } from 'react';
import { AuthService, AuthUser } from '../services/AuthService';

interface LoginPageProps {
  onLoginSuccess: (user: AuthUser) => void;
}

const AnimatedBuildingBackdrop: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <style>{`
      @keyframes loginBackdropPan {
        0%, 100% { transform: scale(1.015) translate3d(0, 0, 0); }
        50% { transform: scale(1.04) translate3d(-10px, -4px, 0); }
      }
      @keyframes loginEnergyLine {
        0% { transform: translateX(-22%); opacity: 0; }
        20%, 74% { opacity: 0.28; }
        100% { transform: translateX(122%); opacity: 0; }
      }
      .login-bg-image { animation: loginBackdropPan 18s ease-in-out infinite; }
      .login-energy-line { animation: loginEnergyLine 10s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        .login-bg-image, .login-energy-line { animation: none; }
      }
    `}</style>
    <div
      className="login-bg-image absolute inset-[-2.5%] bg-cover bg-center opacity-[0.86]"
      style={{
        backgroundImage: "url('/assets/login-architectural-sketch-bw.png')",
      }}
    />
    <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(244,245,246,0.08),rgba(244,245,246,0.22)_48%,rgba(244,245,246,0.54)_100%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_72%_47%,rgba(255,255,255,0.52),transparent_34%)]" />
    <div
      className="absolute inset-0 opacity-[0.18]"
      style={{
        backgroundImage: 'linear-gradient(rgba(23,26,32,.055) 1px, transparent 1px), linear-gradient(90deg, rgba(23,26,32,.055) 1px, transparent 1px)',
        backgroundSize: '96px 96px',
      }}
    />
    <div className="login-energy-line absolute top-[18%] h-px w-1/3 bg-[linear-gradient(90deg,transparent,rgba(15,139,141,0.22),transparent)]" />
  </div>
);

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [facilityName, setFacilityName] = useState('AEES BEMS 시설 관리 플랫폼');
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    usernameRef.current?.focus();
    let cancelled = false;
    void fetch('/api/config/building', { cache: 'no-store' })
      .then(response => response.ok ? response.json() : null)
      .then(profile => {
        if (!cancelled && profile?.meta?.name) setFacilityName(profile.meta.name);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!username.trim() || !password) {
      setError('아이디와 비밀번호를 입력하세요.');
      return;
    }
    setLoading(true);
    setError(null);
    const result = await AuthService.login(username.trim(), password);
    setLoading(false);
    if (result.success && result.user) {
      onLoginSuccess(result.user);
      return;
    }
    setError(result.error ?? '로그인하지 못했습니다.');
    setPassword('');
  };

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-[#f4f5f6] text-[#171a20]">
      <AnimatedBuildingBackdrop />

      <section className="relative z-10 flex min-h-screen items-center justify-center px-5 py-8 md:justify-end md:px-10">
        <div className="w-full max-w-[392px] rounded-lg border border-white/86 bg-white/88 p-6 text-[#171a20] shadow-[0_24px_70px_rgba(17,24,39,0.14)] backdrop-blur-md md:mr-[7vw] md:p-7">
          <div className="mb-7 flex items-start justify-between gap-4">
            <div>
              <p className="max-w-[250px] truncate text-[10px] font-semibold text-[#70767d]" title={facilityName}>{facilityName}</p>
              <h1 className="mt-2 text-[25px] font-semibold leading-tight">운영자 로그인</h1>
            </div>
            <span className="rounded-full border border-[#dfe3e6] bg-white/80 px-2.5 py-1 text-[9px] font-semibold text-[#70767d]">v1.0</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <label className="block">
              <span className="mb-2 block text-[11px] font-semibold text-[#5f6a70]">아이디</span>
              <input
                ref={usernameRef}
                type="text"
                value={username}
                onChange={event => { setUsername(event.target.value); setError(null); }}
                placeholder="아이디 입력"
                autoComplete="username"
                className="h-12 w-full rounded-md border border-[#d6dddc] bg-white px-4 text-[14px] text-[#171a20] outline-none transition-colors placeholder:text-[#a6afb2] focus:border-[#0f8b8d] focus:ring-4 focus:ring-[#0f8b8d]/10"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-[11px] font-semibold text-[#5f6a70]">비밀번호</span>
              <span className="relative block">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={event => { setPassword(event.target.value); setError(null); }}
                  placeholder="비밀번호 입력"
                  autoComplete="current-password"
                  className="h-12 w-full rounded-md border border-[#d6dddc] bg-white px-4 pr-11 text-[14px] text-[#171a20] outline-none transition-colors placeholder:text-[#a6afb2] focus:border-[#0f8b8d] focus:ring-4 focus:ring-[#0f8b8d]/10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(value => !value)}
                  className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-[#77818a] hover:bg-[#edf3f2] hover:text-[#0f8b8d]"
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-[11px]`} />
                </button>
              </span>
            </label>

            {error && (
              <p className="rounded-md border border-[#e5b6b2] bg-[#fff4f2] px-3 py-2 text-[11px] leading-5 text-[#a2473f]">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-[#171a20] text-[12px] font-semibold text-white transition-all hover:bg-[#0f8b8d] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <i className="fa-solid fa-circle-notch fa-spin" />}
              {loading ? '접속 중' : '시스템 접속'}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between border-t border-[#dde4e2] pt-4 text-[10px] text-[#7b858a]">
            <span>AEES BEMS</span>
            <span>접속 기록 저장</span>
          </div>
        </div>
      </section>
    </main>
  );
};
